import { Link, Box, IconButton, Button, createStyles, makeStyles, Paper, Theme, Typography, Dialog } from '@material-ui/core';
import MultilineChartIcon from '@material-ui/icons/MultilineChart';
import React from 'react';
import TransactionReportYears from './TransactionReportYears';
import CloseIcon from '@material-ui/icons/Close';
import { allMonths } from '../util/Months';
import { TransactionStatement } from '../gql-types.generated';
import { DateTime } from 'luxon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      textAlign: 'start',
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(2, 0),
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    contentBox: {
      padding: theme.spacing(3, 3, 1, 3),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerText: {
      height: '100%',
      maxWidth: '85%',
    },
    headerHeadText: {
      letterSpacing: 1.5,
      fontSize: 12,
    },
    headerIcon: {
      lineHeight: 1,
      borderRadius: 50,
      backgroundColor: theme.palette.grey[200],
      padding: 8,
    },
    icon: {
      fontSize: '2rem',
      color: theme.palette.text.secondary,
    },
    footerBox: {
      padding: theme.spacing(1, 3),
      display: 'flex',
      justifyContent: 'flex-end',
      borderTop: '1px solid #E0E0E0',
    },
    footerButton: {
      marginLeft: theme.spacing(2),
    },
    reports: {},
    report: {
      paddingTop: 4,
      cursor: 'pointer',
    },
    helpText: {
      fontSize: '0.8rem',
      fontWeight: 'lighter',
      margin: theme.spacing(1, 0),
    },
  }),
);

interface TransactionReportsProps {
  closeYears: () => void;
  statements?: TransactionStatement[] | null;
  toggleYearsDialogOpen: () => void;
  yearsDialogOpen?: boolean;
}

export const getSpecificYearStatements = (statementsYear: number, tenantStatements?: TransactionStatement[] | null) => {
  //getting the statements for a specific year
  const specificYearStatements = tenantStatements?.filter((statement: TransactionStatement) => {
    const statementYear = DateTime.fromISO(statement.endTime).toUTC().year;
    return statementYear === statementsYear;
  });

  //sorting the statements order
  specificYearStatements?.sort((a: TransactionStatement, b: TransactionStatement) => {
    const aDate = DateTime.fromISO(a.endTime);
    const bDate = DateTime.fromISO(b.endTime);
    return aDate > bDate ? -1 : 1;
  });
  return specificYearStatements || [];
};

const getLastThreeMonthsStatements = (tenantStatements?: TransactionStatement[] | null) => {
  //sorting the statements order
  const statements = tenantStatements?.slice().sort((a: TransactionStatement, b: TransactionStatement) => {
    const aDate = DateTime.fromISO(a.endTime);
    const bDate = DateTime.fromISO(b.endTime);
    return aDate > bDate ? -1 : 1;
  });

  //getting the statements for last three months
  const currentMonth = DateTime.local().month;
  const lastThreeMonths = [];
  const initialMonth = statements && DateTime.fromISO(statements[0].endTime).toUTC().month === currentMonth ? 1 : 0;
  let month = 0;
  while (statements && month < 3 && initialMonth + month < statements?.length) {
    lastThreeMonths.push(statements[initialMonth + month]);
    month += 1;
  }
  return lastThreeMonths;
};

const TransactionReports: React.FC<TransactionReportsProps> = (props: TransactionReportsProps): JSX.Element | null => {
  const classes = useStyles();
  const { closeYears, statements, toggleYearsDialogOpen, yearsDialogOpen } = props;

  if (!statements || !statements.length) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.contentBox}>
          <Box className={classes.header}>
            <Box className={classes.headerText}>
              <Typography variant="subtitle2" className={classes.headerHeadText}>
                MONTHLY TRANSACTION REPORTS
              </Typography>
              <Typography variant="h6">Download Statements</Typography>
            </Box>
            <Box className={classes.headerIcon}>
              <MultilineChartIcon className={classes.icon} />
            </Box>
          </Box>
        </Box>
        <Box className={classes.footerBox}>
          <Typography variant="subtitle2" className={classes.headerHeadText}>
            NO REPORTS AVAILABLE
          </Typography>
        </Box>
      </Paper>
    );
  }
  const months = getLastThreeMonthsStatements(statements);
  const lastThree = [];
  for (let i = 0; i < months.length; i += 1) {
    const statementMonth = DateTime.fromISO(months[i].endTime).toUTC().month;
    const statementYear = DateTime.fromISO(months[i].endTime).toUTC().year;
    lastThree.push(
      <Link key={i} href={months[i].url}>
        <Typography className={classes.report} variant="body1">
          {allMonths[statementMonth - 1].label} {statementYear}
        </Typography>
      </Link>,
    );
  }
  return (
    <Paper className={classes.paper}>
      <Box className={classes.contentBox}>
        <Box className={classes.header}>
          <Box className={classes.headerText}>
            <Typography variant="subtitle2" className={classes.headerHeadText}>
              MONTHLY TRANSACTION REPORTS
            </Typography>
            <Typography variant="h6">Download Statements</Typography>
          </Box>
          <Box className={classes.headerIcon}>
            <MultilineChartIcon className={classes.icon} />
          </Box>
        </Box>
        <Box className={classes.reports} data-cy="last-three-months">
          {lastThree}
        </Box>
        <Typography className={classes.helpText} variant="body1">
          Statements are generated on the 5th of the month.
        </Typography>
      </Box>
      <Box className={classes.footerBox}>
        <Button className={classes.footerButton} color="primary" onClick={toggleYearsDialogOpen} data-cy="view-all-statements">
          VIEW ALL
        </Button>
      </Box>
      <Dialog
        PaperProps={{ className: classes.contentBox }}
        onClose={closeYears}
        fullWidth={true}
        maxWidth={'sm'}
        open={!!yearsDialogOpen}
        disableBackdropClick
        data-cy="monthly-statement-dialog"
      >
        <Box className={classes.header}>
          <Box className={classes.headerText}>
            <Typography variant="h6">Statements</Typography>
            <Typography variant="subtitle1">Your monthly statement will download as a .csv file.</Typography>
          </Box>
          <IconButton aria-label="close" className={classes.closeButton} onClick={closeYears} data-cy="close-monthly-statement-dialog">
            <CloseIcon />
          </IconButton>
        </Box>
        <TransactionReportYears statements={statements} />
      </Dialog>
    </Paper>
  );
};

export default TransactionReports;
